import React from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import LogoHPPlus from './LogoHPPlus';

interface PremiumBadgeProps {
  /**
   * Varianta badge-ului: 'small' pentru chip mic, 'medium' pentru chip normal
   */
  size?: 'small' | 'medium';
  
  /**
   * Varianta visual: 'default' pentru badge standard, 'gradient' pentru badge cu gradient
   */
  variant?: 'default' | 'gradient';
  
  /**
   * Poziția pentru tooltip (opțional)
   */
  tooltip?: string;
  
  /**
   * Show "Plus" text sau doar icon
   */
  showText?: boolean;
}

type PremiumBadgeComponentProps = PremiumBadgeProps & Omit<Partial<ChipProps>, 'tooltip' | 'title' | 'variant' | 'size'>;

/**
 * Componentă PremiumBadge - Badge pentru a indica funcționalități Healthypaws+ Premium
 * 
 * Folosire:
 * ```tsx
 * <PremiumBadge size="small" />
 * <PremiumBadge size="medium" variant="gradient" showText tooltip="Funcționalitate premium" />
 * ```
 */
const PremiumBadge: React.FC<PremiumBadgeComponentProps> = ({
  size = 'small',
  variant = 'default',
  tooltip,
  showText = true,
  ...chipProps
}) => {
  // Culori pentru Healthypaws+ Premium (albastru cald deschis, fără turquoise)
  const premiumColors = {
    default: {
      bg: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)', // Albastru cald deschis
      color: '#ffffff',
      border: 'none'
    },
    gradient: {
      bg: 'linear-gradient(135deg, #5A80C8 0%, #4A70B8 50%, #3A60A8 100%)', // Gradient mai complex
      color: '#ffffff',
      border: 'none'
    }
  };

  const colors = variant === 'gradient' ? premiumColors.gradient : premiumColors.default;

  const sizeConfig = {
    small: {
      height: 20,
      fontSize: '0.65rem',
      iconSize: 12,
      padding: '0 6px'
    },
    medium: {
      height: 24,
      fontSize: '0.75rem',
      iconSize: 16,
      padding: '0 8px'
    }
  };

  const config = sizeConfig[size];

  const badgeContent = (
    <Chip
      icon={showText ? <LogoHPPlus variant="pawPlus" size={config.iconSize} /> : <LogoHPPlus variant="pawPlus" size={config.iconSize} />}
      label={showText ? 'Plus' : ''}
      size={size}
      sx={{
        background: colors.bg,
        color: colors.color,
        fontWeight: 700,
        fontSize: config.fontSize,
        height: config.height,
        padding: config.padding,
        border: colors.border,
        boxShadow: variant === 'gradient' 
          ? '0 2px 8px rgba(90, 128, 200, 0.3)' 
          : '0 1px 4px rgba(74, 144, 226, 0.2)',
        '& .MuiChip-icon': {
          color: colors.color,
          marginLeft: showText ? '4px' : '0',
          marginRight: showText ? '0' : '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        '& .MuiChip-label': {
          padding: showText ? '0 4px' : '0',
          fontWeight: 700,
          letterSpacing: '0.5px'
        },
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: variant === 'gradient'
            ? '0 4px 12px rgba(90, 128, 200, 0.4)'
            : '0 2px 8px rgba(74, 144, 226, 0.3)'
        }
      }}
      {...chipProps}
    />
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {badgeContent}
      </Tooltip>
    );
  }

  return badgeContent;
};

export default PremiumBadge;

